import type { DataJobStepRun } from '../types/data-jobs.types';

export interface DataJobStepTaskResult {
  name: string;
  state: string;
  rowCount: number | null;
  error: string | null;
}

function isRecord(value: unknown): value is Record<string, unknown> {
  return typeof value === 'object' && value !== null && !Array.isArray(value);
}

function taskRowCount(result: unknown): number | null {
  if (typeof result === 'number' && Number.isFinite(result)) {
    return result;
  }
  if (typeof result === 'string' && result.trim() !== '' && Number.isFinite(Number(result))) {
    return Number(result);
  }
  if (isRecord(result) && 'row_count' in result) {
    const raw = result.row_count;
    if (typeof raw === 'number' && Number.isFinite(raw)) {
      return raw;
    }
  }
  if (isRecord(result)) {
    let total = 0;
    let sawInt = false;
    for (const value of Object.values(result)) {
      if (typeof value === 'number' && Number.isFinite(value)) {
        total += value;
        sawInt = true;
      }
    }
    if (sawInt) {
      return total;
    }
  }
  return null;
}

/** Extract per-seat/table task results stored on a platform step. */
export function getStepTaskResults(step: DataJobStepRun): DataJobStepTaskResult[] {
  const metadata = step.output_metadata;
  if (!isRecord(metadata)) {
    return [];
  }
  const platformResult = metadata.platform_result;
  if (!isRecord(platformResult)) {
    return [];
  }
  const tasks = platformResult.tasks;
  if (!Array.isArray(tasks)) {
    return [];
  }

  return tasks.flatMap((task) => {
    if (!isRecord(task)) {
      return [];
    }
    const name = typeof task.name === 'string' ? task.name : null;
    const state = typeof task.state === 'string' ? task.state : null;
    if (!name || !state) {
      return [];
    }
    const error = typeof task.error === 'string' && task.error.trim()
      ? task.error
      : null;
    return [
      {
        name,
        state,
        rowCount: taskRowCount(task.result),
        error,
      },
    ];
  });
}
